import Toast from 'react-native-toast-message'
import ErrorHandler from '../utils/ErrorHandler';
import { supabase, invokeEdgeFunction } from './client';
import { getCurrentOrgID } from './organization';

// ─── Campaigns ───

const getSMSCampaigns = async () => {
    const orgID = await getCurrentOrgID();
    const { data, error } = await supabase
        .from('SMSCampaigns')
        .select('*, Sender:People!sentByID(id, firstName, lastName)')
        .eq('organizationID', orgID)
        .order('createdAt', { ascending: false });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getSMSCampaigns", error }, `Something went wrong loading SMS campaigns`);
        return [];
    }
    return data;
};

const getSMSCampaignRecipients = async (campaignID) => {
    const recipients = [];
    let offset = 0;
    while (true) {
        const { data, error } = await supabase
            .from('SMSCampaignRecipients')
            .select('id, name, phoneNumber, succeeded, notes, createdAt, personID, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
            .eq('campaignID', campaignID)
            .order('createdAt', { ascending: true })
            .order('id', { ascending: true })
            .range(offset, offset + 499);

        if (error) {
            ErrorHandler(Error(error.message), { query: "getSMSCampaignRecipients", error }, `Something went wrong loading SMS campaign recipients`);
            return [];
        }
        recipients.push(...data);
        if (data.length < 500) break;
        offset += 500;
    }
    return recipients.map(recipient => ({
        ...recipient,
        notes: recipient.succeeded ? null
            : recipient.notes?.startsWith('No opted-in phone number') ? 'No opted-in phone number'
            : recipient.notes?.startsWith('Could not normalize phone number') ? 'Invalid phone number'
            : recipient.notes?.startsWith('Duplicate phone') ? 'Duplicate phone number'
            : recipient.notes === 'Not a mobile number' ? 'Not a mobile number'
            : 'Message could not be sent',
    }));
};

const sendSMS = async ({ recipientIDs, message }) => {
    const data = await invokeEdgeFunction('send-sms', {
        body: { recipientIDs, message }
    }, {
        mutation: 'sendSMS',
        context: { recipientIDs, message },
    });

    // Edge function returns immediately with { campaignID, status: 'sending' }.
    // The actual sends run as a background task. Poll the campaign row until
    // it completes (or times out / fails).
    const { campaignID, status, recipientCount = 0, skippedCount = 0 } = data;

    if (status !== 'sending' || !campaignID) {
        // Backward compat: if the function somehow returns synchronously (old
        // deploy), use the data as-is.
        const { sentCount = 0, failedCount = 0 } = data;
        let messageText = `SMS sent to ${sentCount} recipient(s)`;
        if (skippedCount > 0) messageText += ` (${skippedCount} skipped — no SMS number)`;
        if (failedCount > 0) messageText += ` (${failedCount} failed)`;
        Toast.show({ type: sentCount > 0 ? 'success' : 'error', text2: messageText });
        return data;
    }

    Toast.show({ type: 'info', text2: `Sending SMS to ${recipientCount} recipient(s)...` });

    // Poll until the background task finishes. Max ~5 minutes.
    const POLL_INTERVAL_MS = 2000;
    const MAX_POLL_DURATION_MS = 5 * 60 * 1000;
    const startTime = Date.now();
    let campaign = null;

    while (Date.now() - startTime < MAX_POLL_DURATION_MS) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));

        const { data: row, error: pollError } = await supabase
            .from('SMSCampaigns')
            .select('id, status, sentCount, failedCount, skippedCount, recipientCount')
            .eq('id', campaignID)
            .single();

        if (pollError) {
            console.error('send-sms poll error:', pollError);
            continue;
        }

        campaign = row;

        if (campaign.status === 'sent' || campaign.status === 'failed') {
            break;
        }
    }

    if (!campaign || campaign.status === 'sending') {
        // Timed out — the background task may still be running. Show what we
        // know and let the user check the campaign history later.
        Toast.show({ type: 'info', text2: 'SMS campaign is still processing. Check campaign history for results.' });
        return { campaignID, status: 'sending', recipientCount, skippedCount, timedOut: true };
    }

    if (campaign.status === 'failed') {
        Toast.show({ type: 'error', text2: 'SMS campaign failed. Please try again.' });
        return { campaignID, status: 'failed', recipientCount, skippedCount, sentCount: 0, failedCount: campaign.recipientCount };
    }

    // Fetch failed recipient details for display
    let failedRecipients = [];
    if (campaign.failedCount > 0) {
        const { data: failed, error: failedError } = await supabase
            .from('SMSCampaignRecipients')
            .select('name, phoneNumber, notes')
            .eq('campaignID', campaignID)
            .eq('succeeded', false)
            .not('notes', 'like', 'No opted-in phone number')
            .not('notes', 'like', 'Duplicate phone%');

        if (!failedError && failed) {
            failedRecipients = failed.map(r => ({ name: r.name, phone: r.phoneNumber }));
        }
    }

    const { sentCount = 0, failedCount = 0 } = campaign;

    let messageText = `SMS sent to ${sentCount} recipient(s)`;
    if (skippedCount > 0) messageText += ` (${skippedCount} skipped — no SMS number)`;
    if (failedCount > 0) messageText += ` (${failedCount} failed)`;

    Toast.show({ type: sentCount > 0 ? 'success' : 'error', text2: messageText });

    return { campaignID, status: 'sent', sentCount, failedCount, skippedCount, failedRecipients };
};

const sendOptIn = async ({ personID, phoneNumber, previousStatus }) => {
    // Failures here are expected recipient problems (invalid/landline/unreachable
    // number) and the edge function already captures unexpected ones server-side.
    // Toast the actionable message, don't double-report. See CHURCH-APP-1CV.
    const data = await invokeEdgeFunction('send-opt-in', {
        body: { personID, phoneNumber, previousStatus }
    }, {
        mutation: 'sendOptIn',
        context: { personID, phoneNumber, previousStatus },
        reportError: false,
        userFriendlyMessage: `Couldn't send the opt-in text to ${phoneNumber}. The number may be invalid or unable to receive texts.`,
    });

    return data;
};

export { sendSMS, sendOptIn, getSMSCampaigns, getSMSCampaignRecipients };
